import { useEffect, useState, useMemo } from 'react';
import { FieldData } from '@/components/Form/types';
import useLabels from '@/hooks/useApi/useLabels';
import { Select, TextField, SliderPopover } from 'vopay';
import { CircleButton } from 'UI';
import { useForm } from '@/components/Form/utils';
import useFetchClientAccountBalanceRange from '@/hooks/useApi/useFetchClientAccountBalanceRange';
import { useTranslation } from 'react-i18next';

export type FormValues = {
  clientType: 'individual' | 'business' | '';
  accountStatus: 'active' | 'inactive' | '';
  keyword: string;
  label: string;
  balance: [number, number] | null;
};

type UseFilterFormConfigProps = {
  query: Partial<
    Omit<FormValues, 'balance'> & {
      balance: string;
    }
  >;
  onSubmit: (values: FormValues) => void;
};

const initialValues: FormValues = {
  clientType: '',
  accountStatus: '',
  keyword: '',
  label: '',
  balance: null,
};

export function useFilterFormConfig({ query, onSubmit }: UseFilterFormConfigProps) {
  const { labels } = useLabels();
  const { balanceRange, fetchClientAccountBalanceRange } = useFetchClientAccountBalanceRange();
  const { t } = useTranslation('translations', { keyPrefix: 'ClientAccounts' });
  const [searchTerm, setSearchTerm] = useState('');
  const filteredLabels = useMemo(
    () =>
      labels
        .filter((label) => label.toLowerCase().includes(searchTerm.toLowerCase()))
        .slice(0, 100)
        .map((label) => ({ label, value: label })),
    [labels, searchTerm],
  );

  useEffect(() => {
    ctx.setValues(
      Object.entries(query).reduce((acc, [field, val]) => {
        return {
          ...acc,
          [field]: field === 'balance' ? val.split('|').map(Number) : val,
        };
      }, ctx.values),
    );
  }, [query]);

  useEffect(() => {
    init();
  }, []);

  async function init() {
    const res = await fetchClientAccountBalanceRange();

    if (!res.Success) {
      console.error(res.ErrorMessage);

      return;
    }

    if (!query.balance) {
      ctx.setFieldValue('balance', res.range);
    }
  }

  const config: FieldData<FormValues>[] = [
    {
      fieldName: 'clientType',
      getInputState: () => 'optional',
      showField: () => true,
      renderField: ({ getFormikData }) => {
        return (
          <Select
            version="2"
            {...getFormikData<FormValues['clientType']>({})}
            sort={false}
            placeholder={t('type', { defaultValue: 'Type' })}
            showClear
            options={[
              { label: t('ClientAccountType.individual', { defaultValue: 'Individual' }), value: 'individual' },
              { label: t('ClientAccountType.business', { defaultValue: 'Business' }), value: 'business' },
            ]}
          />
        );
      },
    },
    {
      fieldName: 'accountStatus',
      getInputState: () => 'optional',
      showField: () => true,
      renderField: ({ getFormikData }) => {
        return (
          <Select
            version="2"
            {...getFormikData<FormValues['accountStatus']>({})}
            sort={false}
            placeholder={t('status', { defaultValue: 'Status' })}
            showClear
            options={[
              { label: 'Active', value: 'active' },
              { label: 'Inactive', value: 'inactive' },
            ]}
          />
        );
      },
    },
    {
      fieldName: 'label',
      getInputState: () => 'optional',
      showField: () => true,
      renderField: ({ getFormikData }) => {
        return (
          <Select
            version="2"
            {...getFormikData<FormValues['label']>({})}
            sort={false}
            showClear
            placeholder={t('Select.label', { defaultValue: 'Label' })}
            filterable
            options={filteredLabels}
            onFilter={(e) => {
              e.preventDefault();
              e.debounce(() => {
                setSearchTerm(e.filter);
              }, 300);
            }}
          />
        );
      },
    },
    {
      fieldName: 'balance',
      getInputState: () => 'optional',
      showField: () => true,
      serializeField(values) {
        // don't include balance in the filter request; otherwise the response will ONLY
        // return accounts with a balance
        if (values.balance === null) {
          return undefined;
        }

        if (balanceRange[0] === values.balance[0] && balanceRange[1] === values.balance[1]) {
          return undefined;
        }

        return values.balance?.join('|');
      },
      renderField: ({ ctx }) => {
        if (!ctx.values.balance) {
          return null;
        }

        return (
          <SliderPopover
            min={balanceRange[0]}
            max={balanceRange[1]}
            value={ctx.values.balance}
            onApply={(range) => {
              ctx.setFieldValue('balance', range);
            }}
          />
        );
      },
    },
    {
      fieldName: 'keyword',
      getInputState: () => 'optional',
      showField: () => true,
      renderField: ({ getFormikData, ctx }) => {
        return (
          <TextField
            variant="outlineShadow"
            {...getFormikData<FormValues['keyword']>({})}
            placeholder={t('TextField.keyword', { defaultValue: 'Keyword' })}
            elementRight={
              ctx.values.keyword ? (
                <CircleButton
                  type="button"
                  icon="close"
                  circleFill={false}
                  onClick={() => ctx.setFieldValue('keyword', '')}
                  style={{
                    marginRight: '8px',
                  }}
                />
              ) : (
                false
              )
            }
          />
        );
      },
    },
  ];

  const { renderFormElements, ctx } = useForm<FormValues>({
    initialValues,
    config,
    onSubmit,
  });

  return {
    ctx,
    renderFormElements: () => renderFormElements({ config }),
  };
}
